import React, { useState } from 'react';
import dayjs from 'dayjs';
import { Link as RouterLink } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import {
  Alert,
  alpha,
  Box,
  Button,
  Chip,
  Collapse,
  Divider,
  IconButton,
  LinearProgress,
  Link,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  Typography,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';

import { useToast } from '../../../common/hooks/useToast';
import { contactPath } from '../crmPaths';
import { count } from '../manage/manage';
import { EmptyState, LoadingRows, TABLE_SX } from '../manage/ManageParts';
import { StepIcon } from './StepIcon';
import { ConfirmDialog } from './WorkflowPanels';
import { describeWorkflowError, ENROLLMENT_META, EnrollmentCounts, EnrollmentStatus, STEP_META, StepDraft, StepType } from './workflowModel';
import { CRM_ENROLLMENT_ACTIONS, CRM_REMOVE_FROM_WORKFLOW, CRM_WORKFLOW_ENROLLMENTS } from './workflows.queries';

interface Enrollment {
  id: number;
  contactId: number | null;
  contactName: string | null;
  status: EnrollmentStatus;
  currentStep: number;
  nextStepAt: string | null;
  nextStepStatus: string | null;
  originType: string | null;
  enrolledAt: string;
  completedAt: string | null;
  excludedAt: string | null;
  excludedReason: string | null;
}

interface Action {
  id: number;
  position: number;
  type: StepType;
  status: string;
  executeAt: string;
  executedAt: string | null;
  attempts: number;
  result: string | null;
  lastError: string | null;
}

const TABS: Array<{ status: EnrollmentStatus | 'ALL'; label: string; key: keyof EnrollmentCounts }> = [
  { status: 'ALL', label: 'Everyone', key: 'total' },
  { status: 'ACTIVE', label: 'In it', key: 'active' },
  { status: 'COMPLETED', label: 'Finished', key: 'completed' },
  { status: 'EXCLUDED', label: 'Left', key: 'excluded' },
  { status: 'CANCELED', label: 'Taken out', key: 'canceled' },
  { status: 'FAILED', label: 'Failed', key: 'failed' },
];

const PAGE = 25;
const when = (value: string | null) => (value ? dayjs(value).format('D MMM YYYY, h:mm a') : '');

/**
 * Who is in a workflow, newest first: where each one is, when their next step
 * runs, and why anyone left. A row opens to the person's own history in it.
 * Read on the tab's opening and on its controls only: nothing polls.
 */
export const WorkflowPeople = ({
  workflowId,
  counts,
  steps,
  canRemove,
  onChanged,
}: {
  workflowId: number;
  counts: EnrollmentCounts;
  steps: ReadonlyArray<StepDraft>;
  canRemove: boolean;
  onChanged: () => void;
}) => {
  const [tab, setTab] = useState<EnrollmentStatus | 'ALL'>('ALL');
  const [page, setPage] = useState(0);
  const [open, setOpen] = useState<number | null>(null);
  const [removing, setRemoving] = useState<Enrollment | null>(null);
  const toast = useToast();

  const { data, previousData, loading, error, refetch } = useQuery<{ crmWorkflowEnrollments: { total: number; items: Array<Enrollment> } }>(
    CRM_WORKFLOW_ENROLLMENTS,
    {
      variables: { workflowId, status: tab === 'ALL' ? null : tab, skip: page * PAGE, take: PAGE },
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
    }
  );
  const [remove, removal] = useMutation(CRM_REMOVE_FROM_WORKFLOW);
  const shown = (data ?? previousData)?.crmWorkflowEnrollments;

  const stepName = (position: number) => {
    const step = steps[position];
    return step ? `${position + 1}. ${STEP_META[step.type].label}` : `Step ${position + 1}`;
  };

  const takeOut = async () => {
    if (!removing) return;
    try {
      await remove({ variables: { enrollmentId: removing.id } });
      toast.success(`Took ${removing.contactName ?? 'them'} out. Their steps still to come will not run.`);
      setRemoving(null);
      refetch();
      onChanged();
    } catch {
      // Shown in the dialog.
    }
  };

  return (
    <Box sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', overflow: 'hidden' }}>
      <Tabs
        value={tab}
        onChange={(_, next) => {
          setTab(next);
          setPage(0);
          setOpen(null);
        }}
        variant="scrollable"
        allowScrollButtonsMobile
        sx={{ px: 1, borderBottom: '1px solid', borderColor: 'divider' }}
      >
        {TABS.map((t) => (
          <Tab
            key={t.status}
            value={t.status}
            label={
              <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center' }}>
                {t.label}
                <Chip size="small" label={count(counts[t.key])} sx={{ height: 20, fontSize: '0.7rem', fontVariantNumeric: 'tabular-nums' }} />
              </Box>
            }
          />
        ))}
      </Tabs>

      {loading && shown ? <LinearProgress sx={{ height: 2 }} /> : <Box sx={{ height: 2 }} />}
      {error && <Alert severity="error" sx={{ m: 2 }}>{describeWorkflowError(error, 'Could not load who is in it.')}</Alert>}

      {shown && shown.total === 0 && (
        <EmptyState
          icon={<PeopleOutlineIcon />}
          title={tab === 'ALL' ? 'Nobody yet' : 'Nobody here'}
          text={tab === 'ALL' ? 'People appear here as the trigger enrols them, or as you enrol them by hand.' : 'Nobody in this workflow is in this state.'}
        />
      )}

      {(!shown || shown.items.length > 0) && (
        <TableContainer sx={TABLE_SX}>
          <Table size="small" aria-label="People in this workflow">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" />
                <TableCell>Person</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>At</TableCell>
                <TableCell>Next</TableCell>
                <TableCell>Came in</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {!shown && <LoadingRows columns={7} />}
              {shown?.items.map((e) => {
                const meta = ENROLLMENT_META[e.status];
                const expanded = open === e.id;
                return (
                  <React.Fragment key={e.id}>
                    <TableRow hover>
                      <TableCell padding="checkbox">
                        <IconButton size="small" aria-label={`${expanded ? 'Hide' : 'Show'} ${e.contactName ?? 'their'} history`} aria-expanded={expanded} onClick={() => setOpen(expanded ? null : e.id)}>
                          {expanded ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
                        </IconButton>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        {e.contactId ? (
                          <Link component={RouterLink} to={contactPath(e.contactId)} underline="hover" color="inherit">
                            {e.contactName || `Contact #${e.contactId}`}
                          </Link>
                        ) : (
                          e.contactName ?? '—'
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={meta.label} sx={{ height: 22, fontSize: '0.7rem', fontWeight: 600, color: meta.tone, bgcolor: alpha(meta.tone, 0.1) }} />
                        {e.excludedReason && (
                          <Typography variant="caption" color="text.secondary" component="div">
                            {e.excludedReason}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{e.status === 'ACTIVE' ? stepName(e.currentStep) : '—'}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{e.status === 'ACTIVE' && e.nextStepAt ? when(e.nextStepAt) : '—'}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {when(e.enrolledAt)}
                        {e.originType && (
                          <Typography variant="caption" color="text.secondary" component="div">
                            {e.originType.toLowerCase().replace(/_/g, ' ')}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {canRemove && e.status === 'ACTIVE' && (
                          <Button size="small" color="inherit" onClick={() => setRemoving(e)}>
                            Take out
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={7} sx={{ p: 0, borderBottom: expanded ? undefined : 0 }}>
                        <Collapse in={expanded} unmountOnExit>
                          <History enrollmentId={e.id} />
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {shown && shown.total > PAGE && (
        <>
          <Divider />
          <TablePagination component="div" count={shown.total} page={page} onPageChange={(_, p) => setPage(p)} rowsPerPage={PAGE} rowsPerPageOptions={[PAGE]} />
        </>
      )}

      {removing && (
        <ConfirmDialog
          title={`Take ${removing.contactName ?? 'this person'} out?`}
          confirm="Take them out"
          danger
          running={removal.loading}
          error={removal.error ? describeWorkflowError(removal.error) : undefined}
          onConfirm={takeOut}
          onClose={() => setRemoving(null)}
        >
          <span>Their steps still to come will not run. Unless re-entry is on, they cannot come into this workflow again.</span>
        </ConfirmDialog>
      )}
    </Box>
  );
};

/** One person's steps in this workflow, in order: what ran, when, and what it did. */
const History = ({ enrollmentId }: { enrollmentId: number }) => {
  const { data, loading, error } = useQuery<{ crmWorkflowEnrollmentActions: Array<Action> }>(CRM_ENROLLMENT_ACTIONS, { variables: { enrollmentId } });

  if (loading) return <LinearProgress sx={{ m: 2 }} />;
  if (error) return <Alert severity="error" sx={{ m: 2 }}>{describeWorkflowError(error, 'Could not load their history.')}</Alert>;
  const actions = data?.crmWorkflowEnrollmentActions ?? [];
  if (!actions.length) return <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>No steps have been scheduled for them yet.</Typography>;

  return (
    <Box component="ol" sx={{ listStyle: 'none', m: 0, py: 1.5, px: { xs: 2, sm: 7 }, bgcolor: '#fafbfc', display: 'grid', gap: 0 }}>
      {actions.map((a, i) => {
        const tone = STEP_META[a.type]?.tone ?? '#64748b';
        const done = a.status === 'EXECUTED';
        const bad = a.status === 'FAILED';
        return (
          <Box component="li" key={a.id} sx={{ display: 'flex', gap: 1.5, position: 'relative', pb: i === actions.length - 1 ? 0 : 1.5 }}>
            {i < actions.length - 1 && <Box aria-hidden sx={{ position: 'absolute', left: 13, top: 28, bottom: 0, width: 2, bgcolor: '#e2e8f0' }} />}
            <Box
              sx={{
                width: 28,
                height: 28,
                flexShrink: 0,
                borderRadius: '50%',
                display: 'grid',
                placeItems: 'center',
                color: done ? '#fff' : tone,
                bgcolor: bad ? '#dc2626' : done ? tone : alpha(tone, 0.12),
              }}
            >
              <StepIcon type={a.type} fontSize={15} />
            </Box>
            <Box sx={{ minWidth: 0, pt: 0.25 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {a.position + 1}. {STEP_META[a.type]?.label ?? a.type}
                <Box component="span" sx={{ fontWeight: 400, color: 'text.secondary' }}>
                  {' '}
                  · {a.status.toLowerCase()}
                  {a.result ? `: ${a.result}` : ''}
                </Box>
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {a.executedAt ? `Ran ${when(a.executedAt)}` : `Due ${when(a.executeAt)}`}
                {a.attempts > 1 ? ` · ${a.attempts} tries` : ''}
              </Typography>
              {a.lastError && (
                <Typography variant="caption" sx={{ color: '#dc2626', display: 'block' }}>
                  {a.lastError}
                </Typography>
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};
